import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	LinearProgress,
	MenuItem,
	Typography,
	Icon,
	IconButton,
	Box,
	Alert,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ToastContent } from "../../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { useToast } from "../../../../../shared/hooks";
import { ProposalService, ICancelProposalConclude } from "../../../../../shared/services/api/proposal/ProposalService";
import { cancelReasons } from "../helpers/reasonCancel";
import CloseIcon from "@mui/icons-material/Close";

interface ModalCancelProps {
	isClose: () => void;
	isOpenModal: boolean;
	rows: any;
}

export const ModalConcludeCancelProposal: React.FC<ModalCancelProps> = ({ isClose, isOpenModal, rows }) => {
	const toastHandler = useToast();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const { formRef } = useVForm();

	// Datas para validação
	const rawTermBegin = rows?.termBegin?.substring(0, 10) || "";
	const termBeginDate = rawTermBegin ? parseISO(rawTermBegin) : new Date();
	const termBeginLabel = rawTermBegin ? format(termBeginDate, "dd/MM/yyyy") : "data inválida";

	const today = new Date();
	const cancelWindowDays = rows?.renovatedId ? 120 : 60;
	const maxBackdate = new Date(today);
	maxBackdate.setDate(today.getDate() - cancelWindowDays);

	const todayStr = today.toISOString().substring(0, 10);
	const maxBackdateStr = maxBackdate.toISOString().substring(0, 10);
	const termBeginStr = termBeginDate.toISOString().substring(0, 10);

	const formValidationSchemaCancelConclude: yup.SchemaOf<ICancelProposalConclude> = yup.object().shape({
		confirmCanceledType: yup.string().required("Campo Obrigatório"),
		confirmCanceledReason: yup.string().required("Campo Obrigatório"),
		confirmCanceledAt: yup
			.string()
			.required("Campo obrigatório")
			.transform((value, originalValue) => (originalValue === "" ? null : originalValue))
			.nullable()
			.test("is-valid-date", "Data inválida", (value) => {
				if (!value) return false;
				const parsedDate = new Date(value.substring(0, 10));
				return !isNaN(parsedDate.getTime());
			})
			.test("valid-range", (value, context) => {
				if (!value) return true;

				const dateStr = value.substring(0, 10);

				if (dateStr < termBeginStr) {
					return context.createError({
						message: `Data não pode ser anterior ao início da garantia (${termBeginLabel})`,
					});
				}

				if (dateStr < maxBackdateStr) {
					return context.createError({
						message: `Data não pode ser menor que ${cancelWindowDays} dias (${format(maxBackdate, "dd/MM/yyyy")})`,
					});
				}

				if (dateStr > todayStr) {
					return context.createError({
						message: `Data não pode ser maior que hoje (${format(today, "dd/MM/yyyy")})`,
					});
				}

				return true;
			}),
	});

	const handleBlur = async (field: keyof ICancelProposalConclude, value: any) => {
		try {
			await formValidationSchemaCancelConclude.fields[field].validate(value);
			formRef.current?.setFieldError(field, "");
		} catch (error: any) {
			formRef.current?.setFieldError(field, error.message);
		}
	};

	const handleSubmit = async (dados: ICancelProposalConclude) => {
		try {
			await formValidationSchemaCancelConclude.validate(dados, { abortEarly: false });

			setIsLoading(true);
			const result = await ProposalService.updateConfirmCancelId(rows.id, dados);

			if (result instanceof Error) {
				throw new Error(result.message);
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sucesso"} content={"Cancelamento efetivado com sucesso"} />,
			});

			navigate(`/garantias/`);
		} catch (error: any) {
			if (error instanceof yup.ValidationError) {
				const validationErrors: IVFormErros = {};
				error.inner.forEach((err) => {
					if (!err.path) return;
					validationErrors[err.path] = err.message;
				});
				formRef.current?.setErrors(validationErrors);
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={"Erro"} content={error.message} />,
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography variant="h5" sx={{ fontWeight: 300, color: "primary.main" }}>
						Concluir Cancelamento
					</Typography>
					<IconButton onClick={isClose} size="small" color="inherit">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<VForm ref={formRef} onSubmit={handleSubmit} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} placeholder="">
				<DialogContent dividers>
					{isLoading && <LinearProgress />}

					<Alert severity="warning" sx={{ mb: 2 }}>
						Data informada pela imobiliária para o término do Contrato de garantia:{" "}
						<strong>{rows?.canceledAt ? format(new Date(rows.canceledAt + " 03:00:00"), "dd/MM/yyyy") : "Data não informada"}</strong>
					</Alert>

					<Alert severity="warning" sx={{ mb: 2 }}>
						A alteração da data de confirmação de cancelamento do contrato de garantia afetará as parcelas a serem canceladas.
					</Alert>

					<Alert severity="warning" sx={{ mb: 2 }}>
						Caso a garantia tenha alguma bonificação e ou premiação , o cancelamento do contrato irá cancelar todas as bonificações e premiações
						associadas.
					</Alert>

					<Grid container spacing={2}>
						<Grid item xs={12} md={8}>
							<VTextField
								fullWidth
								label="Motivo do cancelamento"
								name="confirmCanceledType"
								disabled={isLoading}
								defaultValue="Outros"
								select
								onBlur={(e) => handleBlur("confirmCanceledType", e.target.value)}
							>
								{cancelReasons.map((reason, index) => (
									<MenuItem key={index} value={reason.value}>
										{reason.label}
									</MenuItem>
								))}
							</VTextField>
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField
								fullWidth
								label="Data de cancelamento"
								name="confirmCanceledAt"
								type="date"
								onBlur={(e) => handleBlur("confirmCanceledAt", e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<VTextField
								fullWidth
								label="Descreva o motivo"
								name="confirmCanceledReason"
								disabled={isLoading}
								multiline
								rows={4}
								onBlur={(e) => handleBlur("confirmCanceledReason", e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button onClick={isClose} color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>}>
						Voltar
					</Button>
					<Button type="submit" color="primary" variant="contained" startIcon={<Icon>check</Icon>} disabled={isLoading}>
						Concluir Cancelamento
					</Button>
				</DialogActions>
			</VForm>
		</Dialog>
	);
};
